import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface WhatsAppCommunication {
  id: number;
  date: string;
  sent_to: string;
  message: string;
  employee: string;
  delivery_status: 'sent' | 'delivered' | 'failed' | 'pending';
  ref_no: string;
}

interface PolicyWhatsAppTabProps {
  policyId: number;
  clientId: string;
  policyNumber?: string;
  clientName?: string;
  user?: { id?: number; username?: string; name?: string; email?: string };
}

interface PolicyDocument {
  id: number;
  file_name: string;
  original_name: string;
  file_path: string;
  description: string;
  created_at: string;
  file_size: number;
  document_type: string;
}

interface PersonOfInterest {
  id: number;
  name: string;
  surname: string;
  contact_number: string;
}

const generateRefNo = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const y = now.getFullYear();
  const m = pad(now.getMonth() + 1);
  const d = pad(now.getDate());
  const h = pad(now.getHours());
  const mi = pad(now.getMinutes());
  const s = pad(now.getSeconds());
  const r = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
  return `REF-${y}${m}${d}-${h}${mi}${s}-${r}`;
};

const API_URL = process.env.REACT_APP_API_URL || '';

const PolicyWhatsAppTab: React.FC<PolicyWhatsAppTabProps> = ({
  policyId,
  clientId,
  policyNumber = '',
  clientName = '',
  user,
}) => {
  const [whatsappMessages, setWhatsAppMessages] = useState<WhatsAppCommunication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState<string>('System');
  const [selectedMessage, setSelectedMessage] = useState<WhatsAppCommunication | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const [clientPhoneNumber, setClientPhoneNumber] = useState<string>('');
  const [personsOfInterest, setPersonsOfInterest] = useState<PersonOfInterest[]>([]);

  const [formData, setFormData] = useState({
    send_to_client: '',
    send_to_poi: '',
    message: '',
  });

  const [recipients, setRecipients] = useState<string[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  const [policyDocuments, setPolicyDocuments] = useState<PolicyDocument[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);

  useEffect(() => {
    fetchWhatsAppMessages();
    fetchContacts();
    fetchPolicyDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, policyId]);

  useEffect(() => {
    if (user) {
      const userName = user.username || user.name || user.email;
      if (userName) {
        setLoggedInUser(userName);
        return;
      }
    }
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const u = JSON.parse(storedUser);
        const name = u.username || u.name || u.email;
        if (name) {
          setLoggedInUser(name);
          return;
        }
      } catch {}
    }
    setLoggedInUser('System');
  }, [user]);

  useEffect(() => {
    if (policyId) {
      setPolicyDocuments([]);
      setSelectedDocuments([]);
    }
  }, [policyId]);

  const fetchWhatsAppMessages = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        setIsLoading(false);
        return;
      }

      const response = await axios.get(
        `${API_URL}/api/whatsapp-policies/communications/${policyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setWhatsAppMessages(response.data.data || response.data || []);
    } catch (err) {
      console.error('Error fetching policy WhatsApp communications:', err);
      setError('Failed to load WhatsApp communications');
      setWhatsAppMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchContacts = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const clientResponse = await axios.get(
        `${API_URL}/api/clients/${clientId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const clientData = clientResponse.data.data || clientResponse.data;
      const phone = clientData.mobile_number || clientData.contact_number || clientData.phone;
      if (phone) setClientPhoneNumber(phone);
    } catch (err) {
      console.warn('Could not fetch client phone number:', err);
    }

    try {
      const poiResponse = await axios.get(
        `${API_URL}/api/persons-of-interest?clientId=${clientId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const persons = Array.isArray(poiResponse.data)
        ? poiResponse.data
        : poiResponse.data.data || [];
      setPersonsOfInterest(persons.filter((p: any) => p.contact_number));
    } catch (err) {
      console.warn('Could not fetch persons of interest:', err);
    }
  };

  const fetchPolicyDocuments = async () => {
    try {
      setIsLoadingDocuments(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(
        `${API_URL}/api/policy-documents/${policyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const raw = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      setPolicyDocuments(
        raw.map((doc: any) => ({
          id: doc.id,
          file_name: doc.original_filename || doc.filename || 'document.pdf',
          original_name: doc.original_filename || doc.filename || 'document.pdf',
          file_path: doc.file_path || '',
          description: doc.description || '',
          created_at: doc.created_at || new Date().toISOString(),
          file_size: doc.file_size || 0,
          document_type: 'policy',
        }))
      );
    } catch (err) {
      console.warn('Could not fetch policy documents:', err);
      setPolicyDocuments([]);
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const handleAddClientRecipient = () => {
    const phone = formData.send_to_client.trim();
    if (!phone) {
      setError('Please select a client phone number');
      return;
    }
    if (recipients.includes(phone)) {
      setError('This phone number is already added');
      return;
    }
    setRecipients([...recipients, phone]);
    setFormData((p) => ({ ...p, send_to_client: '' }));
    setError(null);
  };

  const handleAddPoiRecipient = () => {
    const phone = formData.send_to_poi.trim();
    if (!phone) {
      setError('Please select a person of interest');
      return;
    }
    if (recipients.includes(phone)) {
      setError('This phone number is already added');
      return;
    }
    setRecipients([...recipients, phone]);
    setFormData((p) => ({ ...p, send_to_poi: '' }));
    setError(null);
  };

  const handleRemoveRecipient = (phone: string) =>
    setRecipients(recipients.filter((r) => r !== phone));

  const handleDocumentToggle = (docId: number) =>
    setSelectedDocuments((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );

  const handleViewMessage = (message: WhatsAppCommunication) => {
    setSelectedMessage(message);
    setShowPreviewModal(true);
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatPhoneForWhatsApp = (phoneNumber: string): string | null => {
    if (!phoneNumber) return null;
    let clean = phoneNumber.replace(/[^\d+]/g, '');
    if (clean.startsWith('+')) clean = clean.substring(1);
    else if (clean.startsWith('0') && clean.length === 10) clean = '27' + clean.substring(1);
    else if (!clean.startsWith('27') && clean.length > 9) clean = '27' + clean;
    if (clean.length !== 11 || !clean.startsWith('27')) return null;
    return clean;
  };

  const getClientName = async (): Promise<string> => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/clients/${clientId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const c = response.data.data || response.data;
      return `${c.name || ''} ${c.surname || ''}`.trim() || 'Client';
    } catch {
      return 'Client';
    }
  };

  const resetForm = () => {
    setFormData({ send_to_client: '', send_to_poi: '', message: '' });
    setRecipients([]);
    setSelectedDocuments([]);
  };

  const handleCloseModal = () => {
    resetForm();
    setShowCreateModal(false);
    setError(null);
  };

  const handleSendWhatsApp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (recipients.length === 0) {
      setError('Please add at least one recipient');
      return;
    }
    if (!formData.message.trim()) {
      setError('Message is required');
      return;
    }

    const generatedRefNo = generateRefNo();

    try {
      setIsSubmitting(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        setIsSubmitting(false);
        return;
      }

      const selectedDocs = policyDocuments.filter((d) => selectedDocuments.includes(d.id));

      let fullMessage = formData.message.trim();
      fullMessage = `Policy: ${policyNumber}\n\n${fullMessage}`;
      if (clientName) fullMessage = `Client: ${clientName}\n${fullMessage}`;

      if (selectedDocs.length > 0) {
        fullMessage += '\n\n📎 *Attached Files:*\n';
        selectedDocs.forEach((doc, i) => {
          fullMessage += `${i + 1}. ${doc.original_name || doc.file_name}\n`;
        });
      }

      const payload = {
        to: recipients,
        message: fullMessage,
        client_id: parseInt(clientId),
        policy_id: policyId,
        ref_no: generatedRefNo,
        employee: loggedInUser,
        document_ids: selectedDocuments,
        documents: selectedDocs.map((d) => ({
          id: d.id,
          file_name: d.file_name,
          original_name: d.original_name,
          file_path: d.file_path,
        })),
      };

      const response = await axios.post(
        `${API_URL}/api/whatsapp-policies/send`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 30000,
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to save WhatsApp record');
      }

      const newMessage: WhatsAppCommunication = {
        id: response.data.data?.id ?? Date.now(),
        date: new Date().toISOString(),
        sent_to: recipients.join(', '),
        message: fullMessage,
        employee: loggedInUser,
        delivery_status: 'sent',
        ref_no: generatedRefNo,
      };
      setWhatsAppMessages([newMessage, ...whatsappMessages]);

      const firstRecipient = recipients[0];
      const formattedPhone = formatPhoneForWhatsApp(firstRecipient);
      if (!formattedPhone) {
        setError('Invalid phone number format');
        setIsSubmitting(false);
        return;
      }

      const clientNameForMessage = await getClientName();

      let whatsappMessage = `Hello ${clientNameForMessage},\n\n`;
      whatsappMessage += `Policy: ${policyNumber}\n\n`;
      whatsappMessage += formData.message.trim();

      if (selectedDocs.length > 0) {
        whatsappMessage += '\n\n📎 *Attached Files:*\n';
        selectedDocs.forEach((doc) => {
          const fileName = doc.original_name || doc.file_name || 'Document';
          // TODO: replace with public-token link once /api/public-token is restored.
          // For now list filename only (no clickable URL).
          whatsappMessage += `\n${fileName}\n`;
        });
        whatsappMessage += `\n💡 Please contact us if you need these files emailed.`;
      }

      const encoded = encodeURIComponent(whatsappMessage);
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const waUrl = isMobile
        ? `https://api.whatsapp.com/send?phone=${formattedPhone}&text=${encoded}`
        : `https://web.whatsapp.com/send?phone=${formattedPhone}&text=${encoded}`;

      const newWindow = window.open(waUrl, '_blank');
      if (newWindow) newWindow.focus();

      resetForm();
      setShowCreateModal(false);

      const fileCount = selectedDocs.length;
      const fileMsg = fileCount > 0 ? ` with ${fileCount} file(s)` : '';
      alert(
        `✅ WhatsApp message prepared for ${recipients.length} recipient(s)${fileMsg}. Please click send in WhatsApp.`
      );
    } catch (err: any) {
      console.error('Error saving policy WhatsApp:', err);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to save WhatsApp message';
      setError(errorMessage);
      alert(`❌ ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadgeClass = (status: WhatsAppCommunication['delivery_status']) => {
    const classes: Record<string, string> = {
      sent: 'bg-blue-100 text-blue-800',
      delivered: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
    };
    return classes[status] || classes.pending;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="text-gray-500">Loading WhatsApp communications...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">WhatsApp Communications</h3>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          Send WhatsApp
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {whatsappMessages.length === 0 ? (
        <div className="text-gray-500 text-center py-8">
          No WhatsApp communications found for this policy.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Date', 'Sent To', 'Employee', 'Delivery Status', 'Ref No', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {whatsappMessages.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(m.date).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{m.sent_to}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{m.employee}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(m.delivery_status)}`}
                    >
                      {m.delivery_status.charAt(0).toUpperCase() + m.delivery_status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {m.ref_no || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <button
                      onClick={() => handleViewMessage(m)}
                      className="text-green-600 hover:text-green-800 font-medium text-sm transition-colors flex items-center gap-1"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">WhatsApp Message Preview</h3>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setSelectedMessage(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="bg-green-50 p-4 rounded-md space-y-2">
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">To:</span>
                  <span className="text-sm text-gray-900 flex-1">{selectedMessage.sent_to}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Date:</span>
                  <span className="text-sm text-gray-900 flex-1">
                    {new Date(selectedMessage.date).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Employee:</span>
                  <span className="text-sm text-gray-900 flex-1">{selectedMessage.employee}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Ref No:</span>
                  <span className="text-sm text-gray-900 flex-1 font-mono">{selectedMessage.ref_no || '-'}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-500 w-24">Status:</span>
                  <span className="text-sm flex-1">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(selectedMessage.delivery_status)}`}
                    >
                      {selectedMessage.delivery_status.charAt(0).toUpperCase() + selectedMessage.delivery_status.slice(1)}
                    </span>
                  </span>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Message:</h4>
                <div className="bg-white border border-gray-200 rounded-md p-4 whitespace-pre-wrap text-sm text-gray-800 min-h-[100px] max-h-[300px] overflow-y-auto">
                  {selectedMessage.message || '-'}
                </div>
              </div>
            </div>
            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setSelectedMessage(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Send WhatsApp Message</h3>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSendWhatsApp} className="p-6 space-y-4">
              <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
                <p className="text-sm text-blue-700">
                  <span className="font-semibold">Policy:</span> {policyNumber}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Send to Client Number
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.send_to_client}
                    onChange={(e) => setFormData({ ...formData, send_to_client: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 appearance-none bg-white"
                    disabled={!clientPhoneNumber}
                  >
                    <option value="">
                      {clientPhoneNumber ? 'Select client phone number...' : 'No client phone number found'}
                    </option>
                    {clientPhoneNumber && (
                      <option value={clientPhoneNumber}>Client: {clientPhoneNumber}</option>
                    )}
                  </select>
                  <button
                    type="button"
                    onClick={handleAddClientRecipient}
                    disabled={!formData.send_to_client}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                      formData.send_to_client
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Add
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Send to Person of Interest Number
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.send_to_poi}
                    onChange={(e) => setFormData({ ...formData, send_to_poi: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 appearance-none bg-white"
                    disabled={personsOfInterest.length === 0}
                  >
                    <option value="">
                      {personsOfInterest.length > 0
                        ? 'Select a person of interest...'
                        : 'No persons of interest with phone numbers'}
                    </option>
                    {personsOfInterest.map((person) => (
                      <option key={person.id} value={person.contact_number}>
                        {person.name} {person.surname}: {person.contact_number}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAddPoiRecipient}
                    disabled={!formData.send_to_poi}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                      formData.send_to_poi
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Add
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipients ({recipients.length})
                </label>
                {recipients.length === 0 ? (
                  <div className="p-3 border border-gray-200 rounded-md bg-gray-50 text-gray-400 text-sm">
                    No recipients added yet. Add client or POI phone numbers above.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 p-3 border border-gray-200 rounded-md bg-gray-50">
                    {recipients.map((phone, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                      >
                        {phone}
                        <button
                          type="button"
                          onClick={() => handleRemoveRecipient(phone)}
                          className="ml-1 text-green-600 hover:text-green-800 focus:outline-none"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={4}
                  placeholder="WhatsApp message content..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Files</label>
                {isLoadingDocuments ? (
                  <div className="text-gray-500 text-sm py-2">Loading documents...</div>
                ) : policyDocuments.length === 0 ? (
                  <div className="text-gray-500 text-sm py-2">No documents found for this policy.</div>
                ) : (
                  <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                    {policyDocuments.map((doc) => (
                      <label
                        key={doc.id}
                        className="flex items-start space-x-3 py-2 hover:bg-gray-50 px-2 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedDocuments.includes(doc.id)}
                          onChange={() => handleDocumentToggle(doc.id)}
                          className="mt-1 h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {doc.original_name || doc.file_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {doc.document_type} • {formatFileSize(doc.file_size || 0)}
                            {doc.created_at &&
                              ` • ${new Date(doc.created_at).toLocaleDateString()}`}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                {selectedDocuments.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedDocuments.length} file(s) selected
                  </p>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-500 hover:bg-green-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send WhatsApp'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyWhatsAppTab;